import { DateTime } from 'luxon';

/**
 * Describing an IANA timezone, without shipping the IANA database.
 *
 * This used to live in `src/components/forms/form_builder/reminder/TimezoneHelper.ts` and run on
 * `moment-timezone`. Two of its functions are needed by `src/models/form.ts` — every form with
 * reminders is mapped through `getTimezone`, and `getEmptyReminderConfig` calls
 * `getCurrentTimezone` — and `models/form.ts` is reached from `main.ts` through `formService`. So a
 * form-builder helper put 715 KB of packed timezone rules into the entry chunk for every route,
 * login included, second only to the whole component library.
 *
 * Luxon reads the same rules out of the browser's own `Intl` data, so the payload is zero and it was
 * already a dependency. `TimezoneHelper` keeps `getTimezones()`, the only function that genuinely
 * needs the database — it enumerates every zone name for a picker, and only the form builder's
 * `TimezoneSelector.vue` calls it, behind a lazy route.
 *
 * `offset` is the field that matters and it is identical either way; `src/utils/__tests__/timezone.test.ts`
 * asserts that against `moment-timezone` directly, on DST zones as well as fixed ones.
 */
export interface Timezone {
  value: string;
  label: string;
  offset: number;
  abbrev: string;
  altName: string;
}

/**
 * `(GMT+05:30) Asia/Kolkata` — the form the reminder picker has always shown.
 *
 * Takes the resolved `DateTime` rather than a zone name and an offset, because `getTimezones()`
 * describes all 597 zones through `getTimezone` and resolving each one twice doubled that work for
 * nothing.
 */
const toTimezone = (zone: string, now: DateTime): Timezone => {
  const offsetMinutes = now.isValid ? now.offset : 0;
  const sign = offsetMinutes >= 0 ? '+' : '-';
  const hours = String(Math.floor(Math.abs(offsetMinutes) / 60)).padStart(2, '0');
  const minutes = String(Math.abs(offsetMinutes) % 60).padStart(2, '0');

  return {
    value: zone,
    label: `(GMT${sign}${hours}:${minutes}) ${zone}`,
    offset: offsetMinutes / 60,
    // Nothing in the app reads either of these. Kept because the shape is persisted on forms, and a
    // missing key would read as a deliberate absence rather than an unused field. They do not match
    // what moment's `format('z')`/`format('zz')` used to return — moment gave the same short token
    // for both — which is why `getTimezones()` builds its options through this function rather than
    // formatting its own. Two spellings of one zone in one persisted field is the bug that avoids.
    abbrev: now.isValid ? now.offsetNameShort ?? '' : '',
    altName: now.isValid ? now.offsetNameLong ?? '' : ''
  };
};

/**
 * The timezone a form's reminders are scheduled in.
 *
 * Accepts either a zone name or an already-described `Timezone`, because `FormModel.mapFromBackend`
 * runs over `reminders.timezone` without knowing which the server sent — and may run twice. It also
 * accepts nothing at all: `NetworkReminders.timezone` is typed as required, but the type describes a
 * server response nothing validates, and a reminder config saved before the field existed comes back
 * without it. Dereferencing that threw inside `mapFromBackend`, which fails the whole form list
 * rather than one reminder, so a missing zone falls back to UTC — the same treatment an unrecognised
 * name already gets, and deterministic in a way the viewer's own zone would not be.
 * @param {string | Timezone | null} [tZone] - An IANA zone name, or a `Timezone` to re-derive.
 * @returns {Timezone} The zone, its current UTC offset in hours, and its label.
 */
export const getTimezone = (tZone?: string | Timezone | null): Timezone => {
  const named = typeof tZone === 'string' ? tZone : tZone?.value;
  const zone = typeof named === 'string' && named !== '' ? named : 'UTC';
  const now = DateTime.now().setZone(zone);

  // An unrecognised zone gives luxon an invalid DateTime, whose `offset` is NaN — which would reach
  // the reminder scheduler and the label as `GMTNaN:NaN`. `toTimezone` treats an invalid instance as
  // UTC but keeps the name, so a bad value stays visible instead of being quietly rewritten to the
  // viewer's own zone.
  return toTimezone(zone, now);
};

/**
 * The zone the user is in, for a reminder config that has not chosen one yet.
 * @returns {Timezone} The host's zone, described as `getTimezone` would describe it.
 */
export const getCurrentTimezone = (): Timezone => getTimezone(DateTime.local().zoneName);
